import { useState, useMemo, useCallback, useEffect } from 'react'
import { generateUniqueId } from '../utils'
import { idGenerator, isNewRecord } from '../../../utils/idHelpers'

const useVariableFilters = (initialFilters, collectId, loadVariableValues) => {
  const [variableFilters, setVariableFilters] = useState(initialFilters.variableFilters || [])

  useEffect(() => {
    const filtersWithUniqueIds = (initialFilters.variableFilters || []).map(filter => ({
      ...filter,
      uniqueId: filter.uniqueId || generateUniqueId('vf')
    }))
    setVariableFilters(filtersWithUniqueIds)
    filtersWithUniqueIds.forEach(filter => {
      if (filter.variableId)
        loadVariableValues(filter.variableId)
    })
  }, [initialFilters.variableFilters])

  const activeVariableFilters = useMemo(() =>
    variableFilters.filter(filter => !filter.shouldDestroy), [variableFilters])

  /*
   * The `id` (numeric or 'new-xxx') maps backend validation errors to the right filter.
   * The `uniqueId` is the front-side handle (React keys, handler params) shared with
   * useQuestionFilters where backend `id` doesn't apply.
   * TODO: drop `uniqueId` once useQuestionFilters/FiltersSidebar are harmonised on `id`.
   */
  const addVariableFilter = useCallback(() => {
    setVariableFilters(prev => [
      ...prev,
      {
        id: idGenerator(),
        variableId: '',
        values: [],
        collectId,
        uniqueId: generateUniqueId('vf')
      }
    ])
  }, [collectId])

  const removeVariableFilter = useCallback(uniqueId => {
    setVariableFilters(prev => {
      const newFilters = [...prev]
      const index = newFilters.findIndex(filter => filter.uniqueId === uniqueId)

      if (index === -1)
        return prev

      const filter = newFilters[index]
      if (filter.id && !isNewRecord(filter.id))
        filter.shouldDestroy = true
      else
        newFilters.splice(index, 1)

      return newFilters
    })
  }, [])

  const updateVariableFilter = useCallback((uniqueId, variableId) => {
    setVariableFilters(prev => {
      const newFilters = [...prev]
      const index = newFilters.findIndex(filter => filter.uniqueId === uniqueId)

      if (index === -1)
        return prev

      newFilters[index] = {
        ...newFilters[index],
        variableId,
        values: [],
        collectId
      }

      if (variableId)
        loadVariableValues(variableId)

      return newFilters
    })
  }, [collectId, loadVariableValues])

  const updateVariableValues = useCallback((uniqueId, values) => {
    setVariableFilters(prev => {
      const newFilters = [...prev]
      const index = newFilters.findIndex(filter => filter.uniqueId === uniqueId)

      if (index === -1)
        return prev

      newFilters[index] = {
        ...newFilters[index],
        values,
        collectId
      }

      return newFilters
    })
  }, [collectId])

  const clearVariableFilters = useCallback(() => {
    setVariableFilters(prev =>
      prev
        .map(filter => filter.id && !isNewRecord(filter.id) ? { ...filter, shouldDestroy: true } : null)
        .filter(Boolean)
    )
  }, [])

  return {
    variableFilters,
    activeVariableFilters,
    addVariableFilter,
    removeVariableFilter,
    updateVariableFilter,
    updateVariableValues,
    clearVariableFilters
  }
}

export default useVariableFilters